import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ManualImportForm } from '../useManualImport';

type ActionBarProps = {
    form: ManualImportForm;
};

/** Status / error / validation hint lines plus the Discard, Save-draft and
 *  Import actions (Discard only once a server draft exists). */
export const ActionBar: React.FC<ActionBarProps> = ({ form }) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-col gap-2"
            {...getNodeTestIdProp('manual-import-actions')}
        >
            {form.status ? (
                <p
                    className="text-sm text-success"
                    {...getNodeTestIdProp('manual-import-status')}
                >
                    {t(`purchases.manualImport.status.${form.status}`)}
                </p>
            ) : null}
            {form.saveError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('manual-import-save-error')}
                >
                    {t(form.saveError.labelKey, {
                        message: form.saveError.message,
                    })}
                </p>
            ) : null}
            {form.importError ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('manual-import-import-hint')}
                >
                    {t(form.importError)}
                </p>
            ) : null}
            <div
                className="flex items-center justify-end gap-3"
                {...getNodeTestIdProp('manual-import-actions-buttons')}
            >
                {form.canDiscardDraft ? (
                    <Button
                        isDisabled={form.isSavingDraft || form.isImporting}
                        isPending={form.isDiscardingDraft}
                        onPress={form.onDiscardDraft}
                        variant="danger-soft"
                        {...getNodeTestIdProp('manual-import-discard-draft')}
                    >
                        {t('purchases.manualImport.discardDraft')}
                    </Button>
                ) : null}
                <Button
                    isDisabled={!form.canSaveDraft}
                    isPending={form.isSavingDraft}
                    onPress={form.onSaveDraft}
                    variant="secondary"
                    {...getNodeTestIdProp('manual-import-save-draft')}
                >
                    {t('purchases.manualImport.saveDraft')}
                </Button>
                <Button
                    isDisabled={!form.canImport}
                    isPending={form.isImporting}
                    onPress={form.onImport}
                    variant="primary"
                    {...getNodeTestIdProp('manual-import-import')}
                >
                    {t('purchases.manualImport.import')}
                </Button>
            </div>
        </div>
    );
};
